// Centralized labels/values used by the Homeowner Preferences Quiz

export type Option = { value: string; label: string; helperText?: string };

export const BUDGET_RANGES: Option[] = [
  { value: "3000", label: "$3,000" },                 // default shown on budget step
  { value: "3001_5000", label: "$3,001- $5,000" },
  { value: "5001_10000", label: "$5,001-$10,000" },
  { value: "10001_15000", label: "$10,001 - $15,000" },
  { value: "15001_20000", label: "$15,001 - $20,000" },
  { value: "20001_30000", label: "$20,001 - $30,000" },
  { value: "30001_40000", label: "$30,001- $40,000" },
  { value: "40001_50000", label: "$40,001- $50,000" },
  { value: "50001_75000", label: "$50,001 - $75,000" },
  { value: "75001_150000", label: "$75,001 - $150,000" },
  { value: "150001_PLUS", label: "$150,001 +" },
];

export const BILLING_METHODS: Option[] = [
  { 
    value: "HOURLY", 
    label: "Hourly Rate",
    helperText: 'One of the most straightforward ways interior designers charge is by the hour. Designers often set hourly rates based on their experience and expertise. This method works well for projects that require ongoing consultation, such as styling or updates. However, it\'s essential to understand the total hours required upfront, as this can vary depending on the project\'s complexity.\n\nBest for: Clients who prefer flexibility or have smaller, ongoing design needs.\nConsiderations: Be sure to ask for an estimate of total hours to avoid surprises.'
  },
  { 
    value: "FLAT_FEE", 
    label: "Flat Fee",
    helperText: 'A flat fee or fixed-rate pricing model is when the designer charges a set amount for the entire project, regardless of the time spent. This model is popular for larger projects, such as full-home renovations or commercial spaces, where the designer can anticipate the work involved.\n\nBest for: Homeowners who prefer knowing the total cost upfront.\nConsiderations: Ensure the scope of work is clearly defined in the contract to avoid additional charges.'
  },
  { 
    value: "PERCENT_PROJECT", 
    label: "Percentage of Project Cost",
    helperText: 'Many interior designers charge a percentage of the total project cost, typically ranging from 10% to 30%. This means the designer\'s fee will fluctuate based on the overall budget for materials, furnishings, and construction. This pricing model works well for larger projects where the designer is responsible for significant purchasing decisions.\n\nBest for: High-budget projects where the designer oversees large purchases and installation.\nConsiderations: As costs increase, so will the designer\'s fee. Be sure to set a realistic budget.'
  },
  { 
    value: "PER_SQFT", 
    label: "Per Square Foot Flat Rate",
    helperText: 'Designers may charge by the square foot for larger commercial or residential projects. This model allows the designer to scale their fees based on the size of the space, making it a popular choice for offices, restaurants, and luxury home designs.\n\nBest for: Large-scale commercial projects or expansive residential designs.\nConsiderations: Make sure to clarify what services are included in the per-square-foot price to avoid miscommunication.'
  },
  { 
    value: "ALL_INCLUSIVE_RETAINER", 
    label: "All Inclusive Project Retainer",
    helperText: 'Some interior designers may ask for a design retainer upfront, especially for high-end projects. This retainer often covers the initial consultation, design planning, and other upfront services. Once the retainer is used, the designer may switch to an hourly rate or percentage-based fee.\n\nBest for: Larger or more complex projects that require in-depth planning.\nConsiderations: Ask how the retainer is applied to the overall project and if it\'s refundable.'
  },
  { 
    value: "HYBRID", 
    label: "Hybrid",
    helperText: 'Many designers use a combination of the above models, such as charging an hourly rate for consultations and a flat fee for the design plan. Some may also offer packages that bundle different services, such as sourcing, project management, and installation.\n\nBest for: Projects that require a balance of consultation and full-service design.\nConsiderations: Clearly outline which services are billed under each pricing structure.'
  },
  { 
    value: "DESIGNER_CHOICE", 
    label: "Flexible to Design's Preference",
    helperText: 'You\'re open to the designer choosing the billing method that works best for your specific project. This flexibility allows the designer to recommend the most appropriate pricing structure based on the scope and complexity of your design needs.'
  },
];

export const PARTNERSHIP_STYLES: Option[] = [
  { 
    value: "DESIGNER_LEADS", 
    label: "Designer does it for me, with approvals, but they are the expert",
    helperText: 'In this partnership style, the designer takes the lead on all design decisions while keeping you informed and seeking your approval at key milestones. The designer acts as the expert guide, handling the creative vision, sourcing, and project management while you provide feedback and final approvals.\n\nBest for: Homeowners who trust their designer\'s expertise and prefer a hands-off approach while staying involved in major decisions.'
  },
  { 
    value: "COLLABORATIVE", 
    label: "Collaborative, the designer lets me bring ideas, shop with them",
    helperText: 'A collaborative partnership where you and your designer work together as a team. You can contribute ideas, participate in shopping trips, and actively engage in the design process while the designer provides professional guidance and expertise.\n\nBest for: Homeowners who enjoy being involved in the creative process and want to contribute their own ideas and preferences.'
  },
  { 
    value: "HIGH_INVOLVEMENT", 
    label: "I prefer to be highly involved throughout the entire process",
    helperText: 'This partnership style emphasizes your active participation in every aspect of the design process. You\'ll be deeply involved in decision-making, selections, and project management alongside your designer.\n\nBest for: Homeowners who want maximum control and involvement in their design project from start to finish.'
  },
];

export const DESIGNER_ROLES: Option[] = [
  { 
    value: "FULL_SERVICE", 
    label: "Designer Full Service: Designer creates the design, create drawings, source trade specifications, handle the execution and logistics with my approval.",
    helperText: 'Full-service design includes comprehensive project management from concept to completion. Your designer will create the design, produce drawings, source all materials and furnishings, coordinate with trades, and handle all logistics while keeping you informed and getting your approvals.\n\nBest for: Homeowners who want a complete, turnkey design service with minimal hands-on involvement.'
  },
  { 
    value: "CONSULTANT", 
    label: "Consultant: In person ideas, styling, paint selection or retail shopping.",
    helperText: 'Consultant services focus on providing design advice, ideas, and guidance through in-person consultations. This includes styling recommendations, paint and color selection, and shopping assistance at retail stores.\n\nBest for: Homeowners who want professional design advice but prefer to handle purchasing and project management themselves.'
  },
  { 
    value: "E_DESIGN", 
    label: "E-Design: Designer creates the design electronically, sources the items and I handle the execution and logistics.",
    helperText: 'E-Design (electronic design) provides a complete design plan delivered digitally, including room layouts, furniture selections, and sourcing information. You receive the design files and handle all purchasing, delivery, and installation yourself.\n\nBest for: Tech-savvy homeowners who want professional design guidance but prefer to execute the project independently.'
  },
];

export const SKILL_SETS: string[] = [
  "Accessibility",
  "Adaptability",
  "Attention to Detail",
  "Budget Management",
  "Collaboration",
  "Communication",
  "Creativity",
  "Problem Solving",
  "Project Management",
  "Time Management",
];

export const ACQUISITION_PREFS: Option[] = [
  { 
    value: "DESIGNER_HANDLES_ALL", 
    label: "Designer selects, buys and handles all",
    helperText: 'Your designer takes full responsibility for selecting, purchasing, and managing all furniture and decor items. This includes sourcing, ordering, coordinating deliveries, and handling any returns or exchanges. You simply approve the selections and the designer handles all the logistics.\n\nBest for: Homeowners who want a completely hands-off approach and trust their designer to handle all purchasing decisions and logistics.'
  },
  { 
    value: "CLIENT_SHOPS", 
    label: "I prefer the opportunity to shop for a better price and buy on my own, knowing I will be responsible for delivery and returns",
    helperText: 'You take an active role in shopping and purchasing furniture and decor items. Your designer may provide recommendations or a shopping list, but you handle the actual purchasing, delivery coordination, and any returns. This gives you more control over pricing and allows you to shop around for the best deals.\n\nBest for: Homeowners who enjoy shopping, want to save money by finding deals, and are comfortable managing deliveries and returns themselves.'
  },
  { 
    value: "HYBRID", 
    label: "I prefer a hybrid of some designer and some personal purchases",
    helperText: 'A flexible approach where your designer handles some purchases (typically larger or more complex items) while you handle others (often smaller decor items or items you find on your own). This allows you to balance convenience with personal involvement and cost savings.\n\nBest for: Homeowners who want a mix of designer-managed purchases for key items and personal shopping for other pieces.'
  },
];

export const GENDER_OPTIONS: Option[] = [
  { value: "FEMALE", label: "Female" },
  { value: "MALE", label: "Male" },
  { value: "NON_BINARY", label: "Non-Binary" },
  { value: "NO_PREF", label: "No Preference" },
];

export const PET_OPTIONS: Option[] = [
  { value: "BIRDS", label: "Birds" },
  { value: "CATS", label: "Cats" },
  { value: "DOGS", label: "Dogs" },
  { value: "FISH", label: "Fish" },
  { value: "NO_PREF", label: "No Preference" },
];

export const YES_NO_NOPREF: Option[] = [
  { value: "YES", label: "Yes" },
  { value: "NO", label: "No" },
  { value: "NO_PREF", label: "No Preference" },
];

/** NEW STEPS (11–15) */
export const MINORITY_OPTIONS: Option[] = [
  { value: "ASIAN_PI", label: "Asian American, Pacific Islander" },
  { value: "LATINX", label: "Latinx or Hispanic" },
  { value: "NATIVE_AMERICAN", label: "Native American" },
  { value: "BLACK", label: "Black or African American" },
  { value: "NO_PREF", label: "No Preference" },
];

export const POLITICAL_AFFILIATIONS: Option[] = [
  { value: "NO_PREF", label: "No Preference" },
  { value: "REPUBLICAN", label: "Republican" },
  { value: "DEMOCRAT", label: "Democrat" },
  { value: "LIBERTARIAN", label: "Libertarian" },
  { value: "INDEPENDENT", label: "Independent" },
  { value: "OTHER", label: "Other" },
];

export const EXPERIENCE_OPTIONS: Option[] = [
  { value: "3_5", label: "3 - 5 Years" },
  { value: "6_9", label: "6 - 9 Years" },
  { value: "10_PLUS", label: "10 + Years" },
  { value: "NO_PREF", label: "No Preference" },
];

export const LEAD_READINESS_OPTIONS: Option[] = [
  {
    value: "The thought of doing is by myself is overwhelming",
    label: "The thought of doing is by myself is overwhelming",
  },
  {
    value: "Planning an upcoming event in our home",
    label: "Planning an upcoming event in our home",
  },
  {
    value: "Just moved in and want to personalize our space",
    label: "Just moved in and want to personalize our space",
  },
  {
    value: "Lived in the home for a while and it's time for an update",
    label: "Lived in the home for a while and it's time for an update",
  },
  {
    value: "OTHER",
    label: "Other: Write in",
  },
];

export type PreferencesResult = {
  budgetRange?: string;
  billingMethods?: string[];
  wantsFinancing?: string; // "YES" | "NO"
  partnershipStyle?: string;
  designerRole?: string;
  skillRankings?: string[]; // ordered list
  acquisitionPreference?: string;
  designerGenderPreference?: string;
  designerPetPreference?: string[];
  lgbtqFriendly?: string; // YES/NO/NO_PREF
  womenOwnedFirm?: string; // YES/NO/NO_PREF
  /** new */
  minorityOwnedPreference?: string;
  samePolitics?: string;           // YES/NO/NO_PREF
  politicalAffiliation?: string;   // enum
  politicalOtherText?: string;     // when affiliation === OTHER
  sameAgeRangePreference?: string; // YES/NO/NO_PREF
  desiredAgeRange?: string;        // age range enum
  hasChildrenPreference?: string;  // YES/NO/NO_PREF
  experiencePreference?: string;   // enum
  /** Preferences step 15 ("why are you looking to hire") */
  hireReason?: string;
  /** Legacy/shared field kept for backward compatibility with older records */
  hireLikelihood?: string;
};
